import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, apiError, formatPhone } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Footer from "@/components/Footer";
import { toast } from "sonner";
import {
  LayoutDashboard, Users, CalendarHeart, HandCoins, Mail, LogOut, Search,
  Pencil, Plus, X, Send, Trash2, Target, MapPin, Ticket, ArrowRight, ArrowLeft, Trophy, ShieldCheck,
} from "lucide-react";
import { Seal } from "@/components/Seal";
import { useBranding } from "@/lib/branding";
import AgentToolkit from "@/components/AgentToolkit";

const money = (n) => `$${(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const PAY_METHODS = ["", "check", "cash", "wire", "card", "stock", "online", "other"];
const fmtDate = (s) => { if (!s) return "TBD"; try { return new Date(s).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" }); } catch { return s; } };

const TIER_STYLES = {
  "Highly Engaged": "bg-emerald-100 text-emerald-800",
  "Engaged": "bg-sky-100 text-sky-800",
  "At Risk": "bg-amber-100 text-amber-800",
  "Dormant": "bg-slate-200 text-slate-600",
};
function EngagementBadge({ e }) {
  if (!e) return <span className="text-muted-foreground">—</span>;
  return <span className={`text-[10px] font-mono uppercase px-2 py-0.5 rounded ${TIER_STYLES[e.tier] || TIER_STYLES.Dormant}`} title={`Score ${e.score} · ${e.gifts} gifts · ${e.events} events · ${e.opens} opens`}>{e.tier} · {e.score}</span>;
}

const TABS = [
  { key: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { key: "class", label: "My Class", icon: Users },
  { key: "events", label: "Events", icon: CalendarHeart },
  { key: "fundraising", label: "Fundraising", icon: HandCoins },
  { key: "leaderboard", label: "Leaderboard", icon: Trophy },
  { key: "email", label: "Email Class", icon: Mail },
];

export default function ClassAgent() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const initialTab = params.get("tab");
  const [tab, setTab] = useState(TABS.some((t) => t.key === initialTab) ? initialTab : "dashboard");
  const [ctx, setCtx] = useState(null);

  useEffect(() => {
    const t = params.get("tab");
    if (t && TABS.some((x) => x.key === t)) setTab(t);
  }, [params]);

  const b = useBranding();
  const loadCtx = () => api.get("/agent/context").then((r) => setCtx(r.data)).catch((e) => toast.error(apiError(e.response?.data?.detail)));
  useEffect(() => { loadCtx(); }, []);

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-[#001A4B] text-white">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Seal className="h-10 w-10" />
            <div>
              <div className="font-serif text-lg tracking-tight leading-none">Class Agent Portal</div>
              <div className="text-xs text-white/70 font-mono uppercase tracking-widest mt-1">{b.university_name} · Class of {ctx?.class_year ?? "…"}</div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <button data-testid="agent-to-experience" onClick={() => navigate("/experience")} className="flex items-center gap-1.5 text-sm text-white/80 hover:text-white"><ArrowLeft className="h-4 w-4" /> <span className="hidden sm:inline">Alumni Experience</span></button>
            <span data-testid="class-agent-badge" title="You are a Class Agent" className="inline-flex items-center gap-1.5 rounded-full bg-accent/20 text-accent border border-accent/40 px-2.5 py-1 text-xs font-semibold">
              <ShieldCheck className="h-3.5 w-3.5" /> <span className="hidden xs:inline">Class Agent</span>
            </span>
            <span className="text-sm text-white/80 hidden sm:block">{user?.name}</span>
            <button data-testid="agent-logout" onClick={logout} className="flex items-center gap-1.5 text-sm text-white/80 hover:text-white"><LogOut className="h-4 w-4" /> Sign out</button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6">
        <nav className="flex gap-1 border-b border-border overflow-x-auto">
          {TABS.map((t) => (
            <button key={t.key} data-testid={`agent-tab-${t.key}`} onClick={() => setTab(t.key)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${tab === t.key ? "border-[#C8A24C] text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
              <t.icon className="h-4 w-4" /> {t.label}
            </button>
          ))}
        </nav>

        <main className="py-8">
          {tab === "dashboard" && <Dashboard ctx={ctx} onGo={setTab} />}
          {tab === "class" && <MyClass />}
          {tab === "events" && <EventsTab onChange={loadCtx} />}
          {tab === "fundraising" && <FundraisingTab onChange={loadCtx} />}
          {tab === "leaderboard" && <LeaderboardTab />}
          {tab === "email" && <EmailTab classYear={ctx?.class_year} />}
        </main>
      </div>
      <Footer dark />
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-card border border-border rounded-lg p-5">
      <div className="text-3xl font-serif tracking-tight">{value}</div>
      <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mt-1">{label}</div>
    </div>
  );
}

function Dashboard({ ctx, onGo }) {
  if (!ctx) return <div className="text-muted-foreground">Loading…</div>;
  return (
    <div className="space-y-6" data-testid="agent-dashboard">
      <AgentToolkit onNavigate={onGo} />
      <div>
        <h1 className="font-serif text-3xl tracking-tight">Welcome, {ctx.name?.split(" ")[0] || "Agent"}</h1>
        <p className="text-muted-foreground text-sm mt-1">You're helping steward the <b>Class of {ctx.class_year}</b>. Keep classmates connected, informed, and engaged.</p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <Stat label="Classmates" value={ctx.alumni_count} />
        <Stat label="Claimed" value={ctx.claimed} />
        <Stat label="With Email" value={ctx.with_email} />
        <Stat label="Events" value={ctx.events_count} />
        <Stat label="Gifts" value={ctx.gifts_count} />
        <Stat label="Raised" value={money(ctx.raised)} />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[["class", "Update classmate records", Users], ["events", "Plan a class event", CalendarHeart], ["email", "Email your class", Mail]].map(([k, label, Icon]) => (
          <button key={k} data-testid={`agent-quick-${k}`} onClick={() => onGo(k)} className="text-left bg-card border border-border rounded-lg p-5 hover:border-primary hover:shadow-md transition-all flex items-center gap-3">
            <Icon className="h-6 w-6 text-primary" /><span className="font-medium">{label}</span><ArrowRight className="h-4 w-4 ml-auto text-muted-foreground" />
          </button>
        ))}
      </div>
    </div>
  );
}

function MyClass() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const load = () => api.get(`/agent/alumni?q=${encodeURIComponent(q)}`).then((r) => setRows(r.data.alumni)).catch((e) => toast.error(apiError(e.response?.data?.detail)));
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [q]);

  return (
    <div className="space-y-4" data-testid="agent-class">
      <div className="flex items-center justify-between gap-3">
        <h2 className="font-serif text-2xl tracking-tight">My Class Directory</h2>
        <div className="relative">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input data-testid="agent-class-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search classmates…" className="pl-9 pr-3 py-2 rounded-md border border-input bg-background text-sm w-64" />
        </div>
      </div>
      <div className="bg-card border border-border rounded-lg overflow-hidden overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-muted-foreground text-xs uppercase font-mono"><tr><th className="text-left px-4 py-3">Name</th><th className="text-left px-4 py-3">Email</th><th className="text-left px-4 py-3">Phone</th><th className="text-left px-4 py-3">Engagement</th><th className="text-left px-4 py-3">Status</th><th className="px-4 py-3 text-right">Edit</th></tr></thead>
          <tbody className="divide-y divide-border">
            {rows.map((a) => (
              <tr key={a.id} data-testid={`agent-alumnus-${a.id}`} className="hover:bg-muted/40">
                <td className="px-4 py-3 font-medium">{a.name || "—"}</td>
                <td className="px-4 py-3 text-muted-foreground">{a.email || "—"}</td>
                <td className="px-4 py-3 text-muted-foreground">{formatPhone(a.phone) || "—"}</td>
                <td className="px-4 py-3"><EngagementBadge e={a.engagement} /></td>
                <td className="px-4 py-3"><span className={`text-[10px] font-mono uppercase px-2 py-0.5 rounded ${a.profile_status === "active" ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-600"}`}>{a.profile_status}</span></td>
                <td className="px-4 py-3 text-right"><button data-testid={`agent-edit-${a.id}`} onClick={() => navigate(`/agent/alumni/${a.id}/edit`)} className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-primary"><Pencil className="h-4 w-4" /></button></td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={6} className="px-4 py-12 text-center text-muted-foreground">No classmates found.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const emptyEvent = { title: "", category: "reunion", start: "", end: "", location: { venue: "", city: "", state: "" }, description: "", status: "published" };

function EventsTab({ onChange }) {
  const [events, setEvents] = useState([]);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = () => api.get("/agent/events").then((r) => setEvents(r.data.events)).catch((e) => toast.error(apiError(e.response?.data?.detail)));
  useEffect(() => { load(); }, []);

  const openNew = () => setForm({ ...emptyEvent, location: { ...emptyEvent.location } });
  const openEdit = (e) => setForm({ id: e.id, title: e.title || "", category: e.category || "reunion", start: (e.start || "").slice(0, 16), end: (e.end || "").slice(0, 16), location: { venue: e.location?.venue || "", city: e.location?.city || "", state: e.location?.state || "" }, description: e.description || "", status: e.status || "published" });
  const save = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    setSaving(true);
    try {
      if (form.id) { await api.put(`/agent/events/${form.id}`, form); toast.success("Event updated"); }
      else { await api.post("/agent/events", form); toast.success("Event created"); }
      setForm(null); load(); onChange && onChange();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  const del = async (id) => { if (!window.confirm("Delete this event?")) return; try { await api.delete(`/agent/events/${id}`); toast.success("Deleted"); load(); onChange && onChange(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const invite = async (id) => { if (!window.confirm("Send invitations to your class (skips anyone already invited)?")) return; try { const { data } = await api.post(`/agent/events/${id}/invite`); toast.success(`Invited ${data.sent}${data.skipped ? `, ${data.skipped} already invited` : ""}`); load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };

  return (
    <div className="space-y-4" data-testid="agent-events">
      <div className="flex items-center justify-between">
        <h2 className="font-serif text-2xl tracking-tight">Class Events</h2>
        <button data-testid="agent-new-event" onClick={openNew} className="flex items-center gap-2 px-4 py-2.5 rounded-md bg-primary text-white text-sm font-medium hover:bg-primary/90"><Plus className="h-4 w-4" /> New Event</button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {events.map((e) => (
          <div key={e.id} data-testid={`agent-event-${e.id}`} className="bg-card border border-border rounded-lg p-5">
            <div className="flex items-center justify-between mb-2">
              <span className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase px-2 py-0.5 rounded bg-accent/20 text-primary"><Ticket className="h-3 w-3" /> {e.category}</span>
              <span className="text-[10px] font-mono uppercase px-2 py-0.5 rounded bg-slate-100 text-slate-600">{e.status}</span>
            </div>
            <div className="font-serif text-xl tracking-tight flex items-start gap-2"><CalendarHeart className="h-5 w-5 text-primary shrink-0 mt-0.5" />{e.title}</div>
            <div className="text-sm text-muted-foreground mt-2">{fmtDate(e.start)}</div>
            {(e.location?.venue || e.location?.city) && <div className="flex items-center gap-1.5 text-sm text-muted-foreground mt-1"><MapPin className="h-3.5 w-3.5" />{[e.location?.venue, e.location?.city].filter(Boolean).join(", ")}</div>}
            <div className="mt-3 pt-3 border-t border-border grid grid-cols-3 text-center text-xs">
              <div><div className="text-base font-semibold">{e.summary.invited}</div>Invited</div>
              <div><div className="text-base font-semibold">{e.summary.attending}</div>Attending</div>
              <div><div className="text-base font-semibold">{e.summary.checked_in}</div>Checked-in</div>
            </div>
            <div className="mt-3 flex items-center gap-2">
              <button data-testid={`agent-event-invite-${e.id}`} onClick={() => invite(e.id)} className="flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-md bg-primary text-white hover:bg-primary/90"><Send className="h-3.5 w-3.5" /> Invite Class</button>
              <button data-testid={`agent-event-edit-${e.id}`} onClick={() => openEdit(e)} className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-primary ml-auto"><Pencil className="h-4 w-4" /></button>
              <button data-testid={`agent-event-del-${e.id}`} onClick={() => del(e.id)} className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
        {events.length === 0 && <div className="text-muted-foreground" data-testid="agent-events-empty">No events for your class yet.</div>}
      </div>

      {form && <Modal title={form.id ? "Edit Event" : "New Class Event"} onClose={() => setForm(null)} onSave={save} saving={saving} testid="agent-event">
        <Field label="Title" v={form.title} on={(v) => setForm({ ...form, title: v })} testid="agent-event-title" />
        <div className="grid grid-cols-2 gap-3">
          <Sel label="Category" v={form.category} on={(v) => setForm({ ...form, category: v })} opts={["reunion", "networking", "gala", "lecture", "homecoming", "other"]} />
          <Sel label="Status" v={form.status} on={(v) => setForm({ ...form, status: v })} opts={["draft", "published", "completed", "cancelled"]} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Start" type="datetime-local" v={form.start} on={(v) => setForm({ ...form, start: v })} />
          <Field label="End" type="datetime-local" v={form.end} on={(v) => setForm({ ...form, end: v })} />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Venue" v={form.location.venue} on={(v) => setForm({ ...form, location: { ...form.location, venue: v } })} />
          <Field label="City" v={form.location.city} on={(v) => setForm({ ...form, location: { ...form.location, city: v } })} />
          <Field label="State" v={form.location.state} on={(v) => setForm({ ...form, location: { ...form.location, state: v } })} />
        </div>
        <Field label="Description" v={form.description} on={(v) => setForm({ ...form, description: v })} />
      </Modal>}
    </div>
  );
}

function FundraisingTab({ onChange }) {
  const [campaigns, setCampaigns] = useState([]);
  const [gifts, setGifts] = useState([]);
  const [classmates, setClassmates] = useState([]);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = () => {
    api.get("/agent/campaigns").then((r) => setCampaigns(r.data.campaigns));
    api.get("/agent/gifts").then((r) => setGifts(r.data.gifts));
    api.get("/agent/alumni").then((r) => setClassmates(r.data.alumni));
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.alumnus_id) { toast.error("Choose a classmate"); return; }
    setSaving(true);
    try {
      await api.post("/agent/gifts", { ...form, amount: form.amount ? parseFloat(form.amount) : null });
      toast.success("Gift recorded"); setForm(null); load(); onChange && onChange();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="agent-fundraising">
      <div className="flex items-center justify-between">
        <h2 className="font-serif text-2xl tracking-tight">Fundraising</h2>
        <button data-testid="agent-record-gift" onClick={() => setForm({ alumnus_id: "", amount: "", date: new Date().toISOString().slice(0, 10), campaign_id: "", type: "gift", payment_method: "", notes: "" })} className="flex items-center gap-2 px-4 py-2.5 rounded-md bg-primary text-white text-sm font-medium hover:bg-primary/90"><Plus className="h-4 w-4" /> Record Gift</button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {campaigns.map((c) => {
          const pct = c.goal ? Math.min(100, Math.round((c.raised / c.goal) * 100)) : 0;
          return (
            <div key={c.id} className="bg-card border border-border rounded-lg p-5">
              <div className="flex items-center justify-between"><span className="font-serif text-lg tracking-tight">{c.name}</span><span className="text-[10px] font-mono uppercase px-2 py-0.5 rounded bg-accent/20 text-primary">{c.status}</span></div>
              <div className="mt-3 flex items-center justify-between text-sm"><span className="font-semibold">{money(c.raised)}</span><span className="text-muted-foreground flex items-center gap-1"><Target className="h-3.5 w-3.5" />{c.goal ? money(c.goal) : "—"}</span></div>
              <div className="mt-2 h-2 rounded-full bg-muted overflow-hidden"><div className="h-full bg-accent" style={{ width: `${pct}%` }} /></div>
            </div>
          );
        })}
        {campaigns.length === 0 && <div className="text-muted-foreground">No campaigns.</div>}
      </div>
      <div>
        <h3 className="font-serif text-xl tracking-tight mb-2">Gifts from Your Class</h3>
        <div className="bg-card border border-border rounded-lg overflow-hidden overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-muted-foreground text-xs uppercase font-mono"><tr><th className="text-left px-4 py-2">Date</th><th className="text-left px-4 py-2">Donor</th><th className="text-right px-4 py-2">Amount</th><th className="text-left px-4 py-2">Campaign</th><th className="text-left px-4 py-2">Type</th></tr></thead>
            <tbody className="divide-y divide-border">
              {gifts.map((g) => (
                <tr key={g.id} className="hover:bg-muted/30">
                  <td className="px-4 py-2">{g.date || "—"}</td><td className="px-4 py-2">{g.alumnus_name || "Anonymous"}</td>
                  <td className="px-4 py-2 text-right font-medium">{money(g.amount)}</td><td className="px-4 py-2 text-muted-foreground">{g.campaign_name || "—"}</td>
                  <td className="px-4 py-2"><span className="text-[10px] font-mono uppercase px-2 py-0.5 rounded bg-slate-100 text-slate-600">{g.type === "pledge" ? (g.pledge_fulfilled ? "pledge · paid" : "pledge") : g.type}</span></td>
                </tr>
              ))}
              {gifts.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">No gifts recorded for your class yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {form && <Modal title="Record Gift" onClose={() => setForm(null)} onSave={save} saving={saving} testid="agent-gift">
        <label className="block"><span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Classmate</span>
          <select data-testid="agent-gift-alumnus" value={form.alumnus_id} onChange={(e) => setForm({ ...form, alumnus_id: e.target.value })} className="ud-input mt-1">
            <option value="">— Select classmate —</option>
            {classmates.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </label>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Amount ($)" type="number" v={form.amount} on={(v) => setForm({ ...form, amount: v })} testid="agent-gift-amount" />
          <Field label="Date" type="date" v={form.date} on={(v) => setForm({ ...form, date: v })} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Sel label="Type" v={form.type} on={(v) => setForm({ ...form, type: v })} opts={["gift", "pledge"]} />
          <Sel label="Payment method" v={form.payment_method} on={(v) => setForm({ ...form, payment_method: v })} opts={PAY_METHODS} />
        </div>
        <label className="block"><span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Campaign</span>
          <select value={form.campaign_id} onChange={(e) => setForm({ ...form, campaign_id: e.target.value })} className="ud-input mt-1">
            <option value="">— None —</option>
            {campaigns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </label>
      </Modal>}
    </div>
  );
}

function LeaderboardTab() {
  const [rows, setRows] = useState([]);
  const [myYear, setMyYear] = useState(null);
  useEffect(() => { api.get("/leaderboard").then((r) => { setRows(r.data.rows); setMyYear(r.data.my_year); }).catch(() => {}); }, []);
  return (
    <div className="space-y-4" data-testid="agent-leaderboard">
      <div>
        <h2 className="font-serif text-2xl tracking-tight flex items-center gap-2"><Trophy className="h-6 w-6 text-[#C8A24C]" /> Class Leaderboard</h2>
        <p className="text-muted-foreground text-sm mt-1">How every graduating class ranks by participation and dollars raised. Your class is highlighted.</p>
      </div>
      <div className="bg-card border border-border rounded-lg overflow-hidden overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-muted-foreground text-xs uppercase font-mono"><tr><th className="text-left px-4 py-2">Rank</th><th className="text-left px-4 py-2">Class</th><th className="text-right px-4 py-2">Alumni</th><th className="text-left px-4 py-2">Participation</th><th className="text-right px-4 py-2">Raised</th></tr></thead>
          <tbody className="divide-y divide-border">
            {rows.map((r) => (
              <tr key={r.class_year} data-testid={`leaderboard-row-${r.class_year}`} className={r.is_mine ? "bg-accent/10 font-medium" : "hover:bg-muted/30"}>
                <td className="px-4 py-2.5">{r.rank <= 3 ? ["🥇", "🥈", "🥉"][r.rank - 1] : `#${r.rank}`}</td>
                <td className="px-4 py-2.5">Class of {r.class_year}{r.is_mine && <span className="ml-2 text-[10px] font-mono uppercase px-1.5 py-0.5 rounded bg-[#001A4B] text-white">You</span>}</td>
                <td className="px-4 py-2.5 text-right text-muted-foreground">{r.alumni}</td>
                <td className="px-4 py-2.5">
                  <div className="flex items-center gap-2">
                    <div className="w-24 h-2 rounded-full bg-muted overflow-hidden"><div className="h-full bg-accent" style={{ width: `${r.participation}%` }} /></div>
                    <span className="text-xs">{r.participation}%</span>
                  </div>
                </td>
                <td className="px-4 py-2.5 text-right font-medium">{money(r.raised)}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">No data yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function EmailTab({ classYear }) {
  const [count, setCount] = useState(null);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  useEffect(() => { api.get("/agent/email/count").then((r) => setCount(r.data.count)).catch(() => {}); }, []);

  const send = async () => {
    if (!subject.trim()) { toast.error("Subject is required"); return; }
    if (!window.confirm(`Email your class (${count} recipients)?`)) return;
    setSending(true);
    try {
      const { data } = await api.post("/agent/email", { subject, body });
      toast.success(`Sent to ${data.sent}${data.failed ? `, ${data.failed} failed` : ""}`);
      setSubject(""); setBody("");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setSending(false); }
  };

  return (
    <div className="max-w-2xl space-y-4" data-testid="agent-email">
      <div>
        <h2 className="font-serif text-2xl tracking-tight">Email the Class of {classYear}</h2>
        <p className="text-muted-foreground text-sm mt-1">Reaches <b data-testid="agent-email-count">{count ?? "…"}</b> classmates with a valid email who haven't opted out. An unsubscribe link is added automatically.</p>
      </div>
      <div className="bg-card border border-border rounded-lg p-5 space-y-4">
        <label className="block"><span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Subject</span>
          <input data-testid="agent-email-subject" value={subject} onChange={(e) => setSubject(e.target.value)} className="ud-input mt-1" placeholder="Class of {year} — let's reconnect!" />
        </label>
        <label className="block"><span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Message · use {"{first_name}"} to personalize</span>
          <textarea data-testid="agent-email-body" value={body} onChange={(e) => setBody(e.target.value)} rows={8} className="ud-input mt-1" placeholder={"Dear {first_name},\n\n…"} />
        </label>
        <div className="flex justify-end">
          <button data-testid="agent-email-send" onClick={send} disabled={sending || !count} className="flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary text-white text-sm font-medium hover:bg-primary/90 disabled:opacity-60"><Send className="h-4 w-4" /> {sending ? "Sending…" : `Send to ${count ?? 0}`}</button>
        </div>
      </div>
    </div>
  );
}

function Modal({ title, children, onClose, onSave, saving, testid }) {
  return createPortal((
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 bg-slate-900/60" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-card w-full max-w-lg rounded-lg shadow-xl border border-border overflow-y-auto" style={{ maxHeight: "calc(100vh - 2rem)" }}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-border sticky top-0 bg-card">
          <h2 className="font-serif text-lg tracking-tight">{title}</h2>
          <button data-testid={`${testid}-close`} onClick={onClose} className="p-1.5 rounded-md hover:bg-muted text-muted-foreground"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-6 space-y-4">{children}</div>
        <div className="px-6 py-3 border-t border-border flex justify-end sticky bottom-0 bg-card">
          <button data-testid={`${testid}-save`} onClick={onSave} disabled={saving} className="px-5 py-2 rounded-md bg-primary text-white text-sm font-medium hover:bg-primary/90 disabled:opacity-60">{saving ? "Saving…" : "Save"}</button>
        </div>
      </div>
    </div>
  ), document.body);
}

function Field({ label, v, on, type = "text", testid }) {
  return (
    <label className="block"><span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">{label}</span>
      <input data-testid={testid} type={type} value={v ?? ""} onChange={(e) => on(e.target.value)} className="ud-input mt-1" />
    </label>
  );
}

function Sel({ label, v, on, opts }) {
  return (
    <label className="block"><span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">{label}</span>
      <select value={v} onChange={(e) => on(e.target.value)} className="ud-input mt-1">
        {opts.map((o) => <option key={o} value={o}>{o === "" ? "— None —" : o}</option>)}
      </select>
    </label>
  );
}
